/* wf-tree-story.js
 * Scroll-driven beteende för Plantera träd-sidan.
 *
 *   1. Live-räknaren hämtas från thegoodapi och räknas upp när den syns.
 *   2. SVG-trädet ritas i takt med scrollpositionen (scrub).
 *   3. Pinnade scener byter bild när respektive textpanel passerar mitten.
 *
 * Punkt 2 och 3 körs bara när sektionen har klassen .wf-ts--js, som
 * inline-scriptet i sektionen sätter först om besökaren inte bett om
 * reducerad rörelse. Räknaren körs alltid – bara utan uppräkning.
 *
 * All scroll-läsning sker i en enda rAF-loop, aldrig i scroll-handlern,
 * så att layout-läsningar inte triggar reflow per event.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-wf-tree-story]');
  if (!root) return;

  var animate = root.classList.contains('wf-ts--js');

  /* Sektionen börjar under header och kampanjrad. Utan den här mätningen
     blir hero 100svh hög under dem, och H1:an hamnar under vikningen.
     Taket på 45% skyddar mot en absurt låg hero om headern växer. */
  function setTop() {
    var t = root.getBoundingClientRect().top + (window.pageYOffset || 0);
    root.style.setProperty(
      '--wf-ts-top',
      Math.max(0, Math.min(t, window.innerHeight * 0.45)) + 'px'
    );
  }
  setTop();
  window.addEventListener('resize', setTop, { passive: true });

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  /* Normaliserar p till 0–1 inom fönstret [from, to] */
  function seg(p, from, to) {
    return clamp((p - from) / (to - from), 0, 1);
  }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  /* ------------------------------------------------- 0. entré + avtäckning */

  if (animate) {
    requestAnimationFrame(function () {
      root.classList.add('is-ready');
    });

    var revealIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-on');
            revealIO.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    root.querySelectorAll('[data-wf-reveal]').forEach(function (el, i) {
      el.style.setProperty('--wf-i', i % 6);
      revealIO.observe(el);
    });
  }

  /* ------------------------------------------------------ 1. live-räknaren */

  var counterEl = root.querySelector('[data-wf-count]');

  if (counterEl) {
    var locale = document.documentElement.lang || 'sv';
    var nf;
    try {
      nf = new Intl.NumberFormat(locale);
    } catch (err) {
      nf = new Intl.NumberFormat();
    }

    var target = parseInt(counterEl.getAttribute('data-wf-count'), 10) || 0;
    var started = false;

    var paint = function (n) {
      counterEl.textContent = nf.format(Math.round(n));
    };
    paint(animate ? 0 : target);

    var runCount = function () {
      var dur = 2200;
      var t0 = null;
      var tick = function (ts) {
        if (t0 === null) t0 = ts;
        var t = clamp((ts - t0) / dur, 0, 1);
        paint(target * easeOutExpo(t));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    /* Siffran uppdateras live. Misslyckas hämtningen står serverns
       reservvärde kvar, så räknaren visas aldrig tom eller som noll. */
    var shop = (window.Shopify && window.Shopify.shop) || '';
    if (shop && window.fetch) {
      fetch(
        'https://sprout-app.thegoodapi.com/app/badges/tree_count?shop=' +
          encodeURIComponent(shop) +
          '&locale=' +
          encodeURIComponent(locale) +
          '&hide_tree_count=false&hide_plastics_count=true',
        { mode: 'cors', credentials: 'omit' }
      )
        .then(function (r) {
          return r.ok ? r.text() : Promise.reject(r.status);
        })
        .then(function (html) {
          var m = html.match(/spt-impact-banner-total[^>]*>([^<]+)/);
          if (!m) return;
          var n = parseInt(m[1].replace(/\D/g, ''), 10);
          if (!n) return;
          target = n;
          /* Har uppräkningen redan hunnit landa skrivs siffran om direkt */
          if (started || !animate) paint(n);
        })
        .catch(function () {
          /* tyst – reservvärdet står kvar */
        });
    }

    if (animate) {
      var countIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && !started) {
              started = true;
              countIO.disconnect();
              runCount();
            }
          });
        },
        { threshold: 0.45 }
      );
      countIO.observe(counterEl);
    }
  }

  if (!animate) return;

  /* ------------------------------------------- 2. trädet (scroll-scrubbat) */

  var grow = root.querySelector('[data-wf-grow]');
  var growPaths = [];
  var growLeaves = [];
  var growSteps = [];

  if (grow) {
    growSteps = Array.prototype.slice.call(grow.querySelectorAll('.wf-ts-step'));

    /* Varje streck har ett eget fönster i förloppet: rötter, sedan stam,
       sedan grenar. Fönstren överlappar så att linjerna flyter ihop i
       stället för att ritas strikt en i taget. */
    Array.prototype.slice.call(grow.querySelectorAll('path[data-wf-draw]')).forEach(function (p) {
      var len = p.getTotalLength();
      p.style.strokeDasharray = len;
      p.style.strokeDashoffset = len;
      growPaths.push({
        el: p,
        len: len,
        from: parseFloat(p.getAttribute('data-from')) || 0,
        to: parseFloat(p.getAttribute('data-to')) || 1
      });
    });

    Array.prototype.slice.call(grow.querySelectorAll('ellipse[data-wf-leaf]')).forEach(function (l) {
      growLeaves.push({
        el: l,
        from: parseFloat(l.getAttribute('data-from')) || 0,
        to: parseFloat(l.getAttribute('data-to')) || 1
      });
    });
  }

  /* ------------------------------------------------------ 3. pinnade scener */

  var pillars = root.querySelector('[data-wf-pillars]');

  if (pillars) {
    var figures = Array.prototype.slice.call(pillars.querySelectorAll('.wf-ts-pillars__stage figure'));
    var panels = Array.prototype.slice.call(pillars.querySelectorAll('.wf-ts-panel'));

    if (figures.length) figures[0].classList.add('is-on');

    var panelIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-on');
          var i = panels.indexOf(e.target);
          if (i < 0) return;
          figures.forEach(function (f, j) {
            f.classList.toggle('is-on', j === i);
          });
        });
      },
      /* Smalt band mitt i vyn: bilden byts när textpanelen står centrerad,
         inte när den råkar tangera skärmkanten. */
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    panels.forEach(function (p) {
      panelIO.observe(p);
    });
  }

  /* ------------------------------------------------------- 4. scroll-loop */

  var heroMedia = root.querySelector('[data-wf-hero-media]');
  var ticking = false;

  function frame() {
    ticking = false;
    var vh = window.innerHeight;

    if (heroMedia) {
      var hr = heroMedia.getBoundingClientRect();
      if (hr.bottom > 0 && hr.top < vh) {
        heroMedia.style.transform = 'translate3d(0,' + (-hr.top * 0.18).toFixed(2) + 'px,0)';
      }
    }

    if (grow && growPaths.length) {
      var gr = grow.getBoundingClientRect();
      var span = gr.height - vh;
      if (span > 0 && gr.bottom > 0 && gr.top < vh) {
        var p = clamp(-gr.top / span, 0, 1);
        var i;

        for (i = 0; i < growPaths.length; i++) {
          var g = growPaths[i];
          g.el.style.strokeDashoffset = g.len * (1 - seg(p, g.from, g.to));
        }
        for (i = 0; i < growLeaves.length; i++) {
          var l = growLeaves[i];
          var t = seg(p, l.from, l.to);
          l.el.style.opacity = t;
          l.el.style.transform = 'scale(' + (0.2 + 0.8 * t).toFixed(3) + ')';
        }

        if (growSteps.length) {
          var idx = clamp(Math.floor(p * growSteps.length), 0, growSteps.length - 1);
          for (i = 0; i < growSteps.length; i++) {
            growSteps[i].classList.toggle('is-on', i === idx);
          }
        }
      }
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(frame);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  frame();
})();
